import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll, useMotionTemplate } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1487017159836-4e23ece2e4cf?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";
const SIDE_IMG = "https://images.unsplash.com/photo-1519219788971-8d9797e0928e?crop=entropy&cs=srgb&fm=jpg&w=700&q=80";
const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.2, delay: 0.15 + i * 0.12, ease }}>
      {children}
    </motion.span>
  </span>
);

const TiltImage = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 18 });
  const sx = useTransform(mx, (v) => `${v * 100}%`);
  const sy = useTransform(my, (v) => `${v * 100}%`);
  const spot = useMotionTemplate`radial-gradient(420px circle at ${sx} ${sy}, rgba(255,236,210,0.38), transparent 60%)`;
  const { scrollY } = useScroll();
  const py = useTransform(scrollY, [0, 800], [0, -90]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };

  return (
    <motion.div style={{ y: py }} className="relative [perspective:1400px]">
      <motion.div
        ref={ref} onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-tilt-image"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 18px 18px)" }}
        animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 18px 18px)" }}
        transition={{ duration: 1.4, delay: 0.35, ease }}
        className="arch relative aspect-[4/5] w-full overflow-hidden bg-[var(--bone-2)] shadow-[0_40px_80px_-30px_rgba(28,25,23,0.45)]"
      >
        <motion.img src={HERO_IMG} alt="Bureau minimaliste Kairos" className="h-full w-full object-cover"
          initial={{ scale: 1.25 }} animate={{ scale: 1.02 }} transition={{ duration: 2, delay: 0.35, ease }} />
        <motion.div className="pointer-events-none absolute inset-0 mix-blend-soft-light" style={{ background: spot }} />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-[var(--bone)]" style={{ transform: "translateZ(40px)" }}>
          <span className="font-mono-label text-[11px]">Édition N°07 — Bureau</span>
          <span className="font-display text-2xl italic">2026</span>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 30, rotate: -8 }} animate={{ opacity: 1, y: 0, rotate: -6 }} transition={{ duration: 1.1, delay: 1.1, ease }}
        className="absolute -left-10 bottom-16 hidden w-40 rounded-2xl border border-[var(--line)] bg-white p-2 shadow-xl lg:block"
      >
        <img src={SIDE_IMG} alt="Lampe d'architecte" className="aspect-square w-full rounded-xl object-cover" />
        <p className="mt-2 px-1 text-xs font-medium">Lampe d'architecte</p>
        <p className="px-1 pb-1 text-xs text-[var(--muted)]">Chez Amazon</p>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => (
  <section data-testid="hero-section" className="grain relative overflow-hidden pt-[112px] pb-20 sm:pb-28">
    <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-end gap-14 px-5 sm:px-10 lg:grid-cols-12 lg:gap-10">
      <div className="relative z-10 lg:col-span-7">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.1 }}
          className="font-mono-label mb-8 flex items-center gap-3 text-[11px] sm:text-xs text-[var(--muted)]">
          <span className="h-2 w-2 rounded-full bg-[var(--terra)]" /> Kairos — du grec, « l'instant juste »
        </motion.p>
        <h1 data-testid="hero-title" className="font-display text-[15vw] sm:text-7xl lg:text-[8.2rem] font-light leading-[0.9] tracking-[-0.035em]">
          <Line i={0}>L'essentiel</Line>
          <Line i={1}>pour mieux</Line>
          <Line i={2}><em className="italic text-[var(--terra)]">travailler</em> &amp;</Line>
          <Line i={3}>vivre doux.</Line>
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.9, ease }}
          className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between lg:pr-10">
          <p className="max-w-sm text-base leading-relaxed text-[var(--muted)]">
            Chaises, lampes, accessoires de bureau et outils numériques : notre sélection, à acheter directement chez nos partenaires.
          </p>
          <button data-testid="hero-cta-button" onClick={() => scrollToId("catalogue")}
            className="group inline-flex shrink-0 items-center gap-3 rounded-full bg-[var(--ink)] py-2 pl-6 pr-2 text-sm text-[var(--bone)] transition-colors duration-300 hover:bg-[var(--terra)]">
            Explorer la sélection
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--bone)] text-[var(--ink)] transition-transform duration-500 group-hover:rotate-[-45deg]">
              <ArrowDownRight className="h-4 w-4" />
            </span>
          </button>
        </motion.div>
      </div>
      <div className="relative lg:col-span-5">
        <TiltImage />
      </div>
    </div>
  </section>
);
